import ko from 'knockout';
import FirestoreClient from '../clients/FirestoreClient.js';
import ModalManager from '../utils/ModalManager.js';
import { withTimeout } from '../utils/withTimeout.js';
import { VIEW_TYPES } from '../constants/viewTypes.js';
import IdeaFormViewModel from './IdeaFormViewModel.js';
import ProductViewModel from './ProductViewModel.js';
import ProductRailViewModel from './ProductRailViewModel.js';
import GuideBookViewModel from './GuideBookViewModel.js';
import AboutViewModel from './AboutViewModel.js';
import { escapeHTML } from '../utils/common.js';
import { enableAnalytics } from '../utils/firebase.js';
import { trackDeviceType } from '../services/AnalyticsService.js';
import errorHandler from '../handlers/ErrorHandler.js';
import EmailRevealClient from '../clients/EmailRevealClient.js';
import TransitionManager from '../services/managers/TransitionManager.js';
import NavigationService from '../services/NavigationService.js';
import { STORAGE_KEYS } from '../constants/index.js';

/**
 * AppViewModel - Main application view model for Engryv site
 * 
 * Manages:
 * - View routing and navigation
 * - Language switching and i18n
 * - Modal dialogs
 * - Product and guide content loading
 * - Analytics and cookie settings
 * - Email reveal functionality
 * - Animation and transitions
 * 
 * @class AppViewModel
 * @param {Object} dbInstance - Firestore database instance
 * @param {Object} authInstance - Firebase Auth instance
 */
class AppViewModel {
    /**
     * Initialize the Application ViewModel
     * @constructor
     * @param {Object} dbInstance - Firebase Firestore database instance
     * @param {Object} authInstance - Firebase Authentication instance
     */
    constructor(dbInstance, authInstance) {
        this.db = dbInstance;
        this.auth = authInstance;
        
        // Store references to subscriptions and event listeners for cleanup
        this.subscriptions = [];
        this.eventListeners = {};
        
        // Navigation lock to prevent race conditions during view transitions
        this.navigationLock = Promise.resolve();
        this.pendingNavigation = null;
        
        // Language change debounce timeout to prevent cascading reloads
        this.langChangeTimeout = null;
        
        // Navigation debounce timeout to prevent rapid clicks from queuing multiple requests
        this.navDebounceTimeout = null;
        this.pendingNavSlug = null;
        
        this.modalManager = new ModalManager();
        this.modalVisible = this.modalManager.modalVisible;
        this.modalHtml = this.modalManager.modalHtml;
        this.showModal = (...args) => this.modalManager.showModal(...args);
        this.closeModal = (...args) => this.modalManager.closeModal(...args);

        this.currentLang = ko.observable(document.documentElement.lang || 'en');

        // Observable state for current view
        this.currentViewSlug = ko.observable('');  // Start empty so initial 'home' load works
        this.currentViewData = ko.observable({
            title: window.Engryv.i18n.t('productDetail.loading'),
            htmlContent: '<p>' + window.Engryv.i18n.t('productDetail.loading') + '</p>'
        });
        this.isLoadingView = ko.observable(false);
        this.viewNotFound = ko.observable(false);
        this.currentViewType = ko.observable(VIEW_TYPES.DEFAULT);
        this.isTransitioning = ko.observable(false);

        // Navigation pending state - tracks debounced navigation attempts
        this.pendingNavigation = ko.observable(null);  // Stores setTimeout ID
        this.pendingSlug = ko.observable(null);        // Stores pending slug

        this.currentHeaderTitle = ko.observable('');
        this.mainContentClasses = ko.observable('opacity-100');
        this.logoImgSrc = ko.observable('images/EngryvLogo_small.png');

        // Navigation items for dynamic rendering (supports foreach binding)
        this.navigationItems = [
            { slug: 'home', labelKey: 'nav.home' },
            { slug: 'productsInfo', labelKey: 'nav.products' },
            { slug: 'about', labelKey: 'nav.about' },
            { slug: 'contact', labelKey: 'nav.contact' }
        ];

        this.t = ko.pureComputed(() => {
            return (key, params) => window.Engryv.i18n.t(key, params, this.currentLang());
        });

        // Cookie state management - single source of truth
        this.cookieConsent = ko.observable(localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT)); // 'accepted' | 'settings' | null
        this.cookieBannerVisible = ko.observable(!localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT));
        
        // Subscribe to sync cookieConsent with localStorage and update banner visibility
        this.cookieConsent.subscribe((val) => {
            if (val) {
                localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, val);
            } else {
                localStorage.removeItem(STORAGE_KEYS.COOKIE_CONSENT);
            }
            this.cookieBannerVisible(!val);
        });

        // Cookie banner text computed observables
        this.cookieBannerText = ko.computed(() => {
            return window.Engryv.i18n.t('cookie.bannerText');
        });

        this.cookieAcceptButtonText = ko.computed(() => {
            return window.Engryv.i18n.t('cookie.accept');
        });

        this.cookieSettingsButtonText = ko.computed(() => {
            return window.Engryv.i18n.t('cookie.settings');
        });

        this.allowAnalyticsCookies = ko.observable(localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === 'accepted');
        const analyticsSub = this.allowAnalyticsCookies.subscribe((val) => {
            if (val) {
                localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, 'accepted');
                // Initialize Firebase analytics (legacy)
                if (typeof window.enableAnalytics === 'function') {
                  window.enableAnalytics();
                } else if (typeof window.app !== 'undefined' && typeof window.app.enableAnalytics === 'function') {
                  window.app.enableAnalytics();
                } else if (typeof window.enableAnalytics === 'undefined' && typeof window.appViewModel !== 'undefined' && typeof window.appViewModel.enableAnalytics === 'function') {
                  window.appViewModel.enableAnalytics();
                } else if (typeof enableAnalytics === 'function') {
                  enableAnalytics();
                }
                // Initialize new analytics service
                if (typeof window.Engryv !== 'undefined' && typeof window.Engryv.initializeAnalytics === 'function') {
                  window.Engryv.initializeAnalytics();
                  // Track device type when user accepts analytics
                  trackDeviceType();
                }
            } else {
                localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, 'settings');
            }
        });
        this.subscriptions.push(analyticsSub);

        const cookieSlugSub = this.currentViewSlug.subscribe((slug) => {
            if (slug === 'cookie-settings') {
                this.allowAnalyticsCookies(localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === 'accepted');
            }
        });
        this.subscriptions.push(cookieSlugSub);
        
        // Create storage event handler and store reference for cleanup
        this.eventListeners.storageHandler = () => {
            if (this.currentViewSlug() === 'cookie-settings') {
                this.allowAnalyticsCookies(localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === 'accepted');
            }
        };
        window.addEventListener('storage', this.eventListeners.storageHandler);
        
        // Setup cookie accept button - will be properly cleaned up before adding new listeners
        this.#setupCookieAcceptButton();

        this.currentViewData.subscribe((newData) => {
            // Safely clean up old email reveal listeners before setting up new ones
            try {
                if (this.emailRevealService && typeof this.emailRevealService.cleanup === 'function') {
                    this.emailRevealService.cleanup();
                }
            } catch (error) {
                console.warn('[AppViewModel] Error cleaning up email reveal service:', error);
            }
            
            const siteContentSlugs = [
                'about', 'contact', 'cookie-settings', 'home', 'next-idea-blog',
                'privacy-policy', 'productsInfo', 'terms-of-service'
            ];
            if (siteContentSlugs.includes(this.currentViewSlug())) {
                if (newData && newData.title) {
                    document.title = newData.title;
                    const headerTitle = document.getElementById('engryv-header-title');
                    if (headerTitle) headerTitle.textContent = newData.title;
                }
                if (newData && newData.metaDescription) {
                    let meta = document.querySelector('meta[name="description"]');
                    if (!meta) {
                        meta = document.createElement('meta');
                        meta.name = 'description';
                        document.head.appendChild(meta);
                    }
                    meta.content = newData.metaDescription || 'Engryv: Innovative ideas, products, and support.';
                }
            }
            
            // Safely setup email reveal buttons for the current view
            try {
                if (typeof this.#setupEmailRevealsForView === 'function') {
                    this.#setupEmailRevealsForView(this.currentViewSlug());
                }
            } catch (error) {
                errorHandler.handle(error, { stage: 'setupEmailRevealsForView' }, 'warn');
            }
        });

        this.ideaFormViewModel = new IdeaFormViewModel(this.currentLang);

        this.productViewModel = new ProductViewModel(this.currentLang);
        this.displayCards = this.productViewModel.displayCards;
        this.selectedProductType = this.productViewModel.selectedProductType;
        this.availableProductTypes = this.productViewModel.availableProductTypes;
        this.showSuggestionCard = this.productViewModel.showSuggestionCard;
        this.filteredDisplayCards = this.productViewModel.filteredDisplayCards;
        this.currentProduct = this.productViewModel.currentProduct;
        this.currentImageIndex = this.productViewModel.currentImageIndex;
        this.goToImage = (...args) => this.productViewModel.goToImage(...args);
        this.getProductTypeBorder = (...args) => this.productViewModel.getProductTypeBorder(...args);
        this.getProductDetailBgColor = (...args) => this.productViewModel.getProductDetailBgColor(...args);
        this.nextImage = (...args) => this.productViewModel.nextImage(...args);
        
        // Issue 1.2: Expose loading state from ProductViewModel to template
        this.isLoadingCards = this.productViewModel.isLoadingCards;
        
        // Issue 1.4: Expose error state from ProductViewModel to template
        this.cardLoadError = this.productViewModel.cardLoadError;

        // Expose loading context helper from ProductViewModel for context-aware loading messages
        this.getCurrentLoadingContext = (...args) => this.productViewModel.getCurrentLoadingContext(...args);

        this.productRailViewModel = new ProductRailViewModel(this.currentLang);
        // Link product type filter to header rail so it only shows products from selected type
        this.productRailViewModel.setSelectedProductType(this.productViewModel.selectedProductType);
        this.railProducts = this.productRailViewModel.railProducts;
        this.isRailVisible = this.productRailViewModel.isRailVisible;

        this.guideSearchQuery = ko.observable('');
        this.faqsSearchQuery = ko.observable('');
        
        this.guideBookViewModel = new GuideBookViewModel(this.currentLang, this.guideSearchQuery, this.faqsSearchQuery);
        this.currentGuideBookTab = ko.observable('guide');
        this.guideBookTransitioning = ko.observable(false);
        this.selectedGuideCategory = ko.observable(null);
        this.selectedFaqsCategory = ko.observable(null);

        // Links inside guidebook content (HTML stored in Firestore) can open another category:
        // <a href="#" data-guidebook="faq:account-billing"> or data-guidebook="guide:features"
        this.eventListeners.guideBookLinkHandler = (event) => {
            const link = event.target.closest && event.target.closest('a[data-guidebook]');
            if (!link) return;
            event.preventDefault();
            const [type, categoryId] = link.dataset.guidebook.split(':');
            this.openGuideBookCategory(type, categoryId);
        };
        document.addEventListener('click', this.eventListeners.guideBookLinkHandler);

        // About view model for About page logic and state
        this.aboutViewModel = new AboutViewModel();

        // Initialize service classes for better separation of concerns
        this.emailRevealService = new EmailRevealClient(this.t());
        this.transitionManager = new TransitionManager();
        this.navigationService = new NavigationService(
            this.productViewModel,
            this.guideBookViewModel,
            this.productRailViewModel,
            this.t()
        );

        this.escapeHTML = escapeHTML;
        this.getIdeasCollectionName = (...args) => this.ideaFormViewModel.getIdeasCollectionName(...args);
        this.loadIdeas = (...args) => this.ideaFormViewModel.loadIdeas(...args);

        this.currentLang.subscribe((newLang) => {
            // Clear any pending language change handler to debounce rapid switches
            if (this.langChangeTimeout) {
                clearTimeout(this.langChangeTimeout);
            }
            
            // Debounce language changes by 300ms to prevent cascading reloads
            // Rapid language switches won't trigger multiple network requests
            this.langChangeTimeout = setTimeout(() => {
                const currentSlug = this.currentViewSlug();
                
                // Only reload for content-heavy pages that need fresh content in new language
                const contentHeavyPages = ['next-idea-blog', 'contact', 'about', 'home', 'productsInfo'];
                
                if (contentHeavyPages.includes(currentSlug)) {
                    // For idea form, reload ideas in new language
                    if (currentSlug === 'next-idea-blog' && this.ideaFormViewModel &&
                        typeof this.ideaFormViewModel.loadIdeas === 'function') {
                        this.ideaFormViewModel.loadIdeas();
                    }
                    
                    // Reload the view content for translated static pages
                    this.loadView(currentSlug);
                }
                
                // For product/guide detail pages, language change is already handled by those VMs
                // For home and productsInfo, productViewModel/productRailViewModel already react to language changes
                // For other pages, Knockout i18n bindings handle translation automatically
            }, 300);
        });
    }

    /**
     * Switch the guidebook to a tab and open one of its categories
     * Uses the same fade as the Guide/FAQs tab buttons in guideBook-template
     * @param {string} type - 'guide' or 'faq'
     * @param {string} categoryId - Category id from guidebooks/_metadata (e.g. 'account-billing')
     */
    openGuideBookCategory = (type, categoryId) => {
        const tab = type === 'faq' ? 'faqs' : 'guide';
        const load = () => (type === 'faq'
            ? this.guideBookViewModel.loadFaqContent(null, categoryId)
            : this.guideBookViewModel.loadGuideContent(null, categoryId));
        if (this.currentGuideBookTab() === tab) {
            load();
            return;
        }
        this.guideBookTransitioning(true);
        setTimeout(() => {
            this.currentGuideBookTab(tab);
            load();
            setTimeout(() => this.guideBookTransitioning(false), 50);
        }, 350);
    }

    /**
     * Open the category named in the URL's ?open=guide:<id> or ?open=faq:<id> (links from the apps), then drop the
     * parameter so a refresh or a later visit starts normally
     * @private
     */
    #openGuideBookCategoryFromUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const open = params.get('open');
        if (!open) return;
        const [type, categoryId] = open.split(':');
        if ((type === 'guide' || type === 'faq') && categoryId) {
            this.openGuideBookCategory(type, categoryId);
        }
        params.delete('open');
        const query = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : ''));
    }

    initializeIdeaForm = () => {
        this.ideaFormViewModel.initializeIdeaForm();
    }

    /**
     * Load site content view (legal pages, contact, about, etc.)
     * Fetches content from Firestore siteContent collection
     * 
     * @async
     * @param {string} slug - Page slug (e.g., 'privacy-policy', 'contact', 'home')
     * @throws {Error} If Firestore is not initialized or content not found
     */
    loadSiteContentView = async (slug) => {
        if (!this.db) { 
            this.currentViewData({ title: "Error", htmlContent: `<p>${window.Engryv.i18n.t('productDetail.notFound')}</p>` });
            this.viewNotFound(true);
            return; 
        }
        if (!slug) slug = 'home';
        const lang = this.currentLang();
        try {
            const pageData = await withTimeout(
                FirestoreClient.getDocument("siteContent", slug),
                10000,
                `loadSiteContentView: getDocument(${slug})`
            );
            let langContent = pageData.content ? pageData.content[lang] : null;
            if (!langContent && lang !== 'en' && pageData.content && pageData.content['en']) {
                langContent = pageData.content['en'];
            }
            if (!langContent) {
                this.currentViewData({ title: window.Engryv.i18n.t('productDetail.notFound'), htmlContent: `<p>${window.Engryv.i18n.t('productDetail.notFound')}</p>` });
                this.currentViewType(pageData.viewType || 'default');
                this.viewNotFound(true);
            } else {
                let htmlContentToDisplay = langContent.htmlContent || `<p>${window.Engryv.i18n.t('productDetail.noDescription')}</p>`;
                if (slug === 'about' && pageData.sideImage) {
                    const sideImageUrl = pageData.sideImage.endsWith('.png') || pageData.sideImage.endsWith('.jpg') || pageData.sideImage.endsWith('.jpeg') ? pageData.sideImage : pageData.sideImage + '.png';
                    const sideImageAlt = pageData.sideImageAlt || 'About image';
                    htmlContentToDisplay = `
                      <div class="w-full flex flex-col md:flex-row items-stretch justify-between gap-6" style="min-width:0;">
                        <div class="flex-1 min-w-0 flex flex-col justify-center">
                          ${htmlContentToDisplay}
                        </div>
                        <div class="hidden md:block border-l border-slate-200 my-8" style="margin-left:0.5rem;margin-right:0.5rem;"></div>
                        <div class="w-auto md:flex-shrink-0 flex items-center justify-center" style="max-width:480px;">
                          <img src="${sideImageUrl}" alt="${this.escapeHTML(sideImageAlt)}" class="rounded-2xl shadow-md max-w-full max-h-[32rem] object-contain bg-white" style="background:rgba(255,255,255,0.7);" />
                        </div>
                      </div>
                    `;
                }
                const viewDataToDisplay = {
                    title: langContent.title || window.Engryv.i18n.t('productDetail.loading'),
                    htmlContent: htmlContentToDisplay,
                    metaDescription: langContent.metaDescription || pageData.metaDescription || 'Engryv: Innovative ideas, products, and support.'
                };
                this.currentViewData(viewDataToDisplay);
                this.currentViewType(pageData.viewType || VIEW_TYPES.DEFAULT);
                this.viewNotFound(false);
                if (slug === 'next-idea-blog' && window.appViewModel && window.appViewModel.ideaFormViewModel && typeof window.appViewModel.ideaFormViewModel.initializeIdeaForm === 'function') {
                    // Wait for Knockout.js to apply the html binding
                    setTimeout(() => {
                        try {
                            window.appViewModel.ideaFormViewModel.initializeIdeaForm();
                        } catch (formErr) {
                            errorHandler.handle(formErr, { slug, stage: 'initializeIdeaForm' }, 'error');
                        }
                    }, 50);
                }
            }
        } catch (err) {
            // Handle timeout errors specifically
            if (err.code === 'timeout') {
                errorHandler.handle(err, { slug, stage: 'loadSiteContentView-timeout' }, 'warn');
                this.currentViewData({ 
                    title: "Loading Timeout", 
                    htmlContent: `<p>${window.Engryv.i18n.t('productDetail.loadTimeout') || 'The page took too long to load. Please refresh and try again.'}</p>` 
                });
            } else {
                // Handle other errors
                errorHandler.handle(err, { slug, stage: 'loadSiteContentView' }, 'error');
                this.currentViewData({ 
                    title: "Error", 
                    htmlContent: `<p>${window.Engryv.i18n.t('productDetail.notFound')}</p>` 
                });
            }
            this.currentViewType(VIEW_TYPES.DEFAULT);
            this.viewNotFound(true);
        } finally {
            this.isLoadingView(false);
        }
    };

    setFavicon(src) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.type = 'image/png';
        link.href = src;
    }

    /**
     * Handle accept cookies button click
     */
    acceptCookies = () => {
        this.cookieConsent('accepted');
        this.allowAnalyticsCookies(true);
        
        // Enable analytics if available
        if (typeof enableAnalytics === 'function') {
            enableAnalytics();
        }
    };

    /**
     * Handle open cookie settings button click
     */
    openCookieSettings = () => {
        this.cookieConsent('settings');
        this.loadView('cookie-settings');
    };

    /**
     * Setup cookie accept button listener (called once on init)
     * @private
     */
    #setupCookieAcceptButton() {
        if (this.eventListeners.cookieButtonHandler) {
            return; // Already set up
        }
        
        this.eventListeners.cookieButtonHandler = () => {
            if (this.currentViewSlug() === 'cookie-settings') {
                this.allowAnalyticsCookies(true);
            }
        };
        
        // Use requestAnimationFrame instead of setTimeout(0) for better performance
        requestAnimationFrame(() => {
            const acceptBtn = document.getElementById('cookie-accept-btn');
            if (acceptBtn && !acceptBtn.dataset.listenerAttached) {
                acceptBtn.addEventListener('click', this.eventListeners.cookieButtonHandler);
                acceptBtn.dataset.listenerAttached = 'true';
            }
        });
    }

    /**
     * Get Cloud Function URL from environment or reconstruct from projectId
     * @param {string} functionName - Name of the Cloud Function
     * @returns {string} Full URL to Cloud Function
     */
    #getCloudFunctionUrl(functionName) {
        // Try environment variable first (most secure approach)
        const envKey = `VITE_CLOUD_FUNCTION_${functionName.toUpperCase()}_URL`;
        if (import.meta.env[envKey]) {
            return import.meta.env[envKey];
        }
        
        // For backwards compatibility, try to get projectId and region from env
        const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
        const region = import.meta.env.VITE_CLOUD_FUNCTION_REGION || 'us-central1';
        
        if (!projectId) {
            throw new Error(`Cloud Function URL not configured for ${functionName}. Set ${envKey} or VITE_FIREBASE_PROJECT_ID.`);
        }
        
        // Reconstruct URL from projectId with configurable region (fallback)
        // Region defaults to 'us-central1' if not explicitly set
        return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
    }

    /**
     * Display error view with appropriate message based on error type
     * Consolidates error display logic to avoid repetition
     * @private
     * @param {string} errorType - Type of error: 'form', 'content', or 'generic'
     */
    #displayErrorView(errorType = 'generic') {
        const errorMessages = {
            generic: 'An unexpected error occurred. Please refresh the page.',
            content: 'Error loading content. Please try again or contact support.',
            form: 'Error loading form. Please try again or contact support.'
        };
        
        this.currentViewData({
            title: 'Error',
            htmlContent: `<p>${errorMessages[errorType]}</p>`
        });
        this.viewNotFound(true);
    }

    /**
     * Setup email reveal buttons based on current view
     * Centralizes email button configuration to avoid repetition
     * @private
     */
    #setupEmailRevealsForView(viewSlug) {
        switch (viewSlug) {
            case 'terms-of-service':
                this.emailRevealService.setupEmailRevealButton(
                    'getAdminEmailBtn',
                    this.#getCloudFunctionUrl('getAdminEmail'),
                    'termsOfService'
                );
                break;
            case 'next-idea-blog':
                this.emailRevealService.setupEmailRevealButton(
                    'getOfficeEmailBtn',
                    this.#getCloudFunctionUrl('getOfficeEmail'),
                    'nextIdeaBlog'
                );
                break;
            case 'contact':
                this.emailRevealService.setupEmailRevealButton(
                    'getOfficeEmailBtn',
                    this.#getCloudFunctionUrl('getOfficeEmail'),
                    'contact',
                    'showOfficeEmail',
                    'loadingOfficeEmail'
                );
                this.emailRevealService.setupEmailRevealButton(
                    'getAdminEmailBtn',
                    this.#getCloudFunctionUrl('getAdminEmail'),
                    'contact',
                    'showAdminEmail',
                    'loadingAdminEmail'
                );
                break;
        }
    }

    #updateLogo(viewSlug) {
        this.logoImgSrc('images/EngryvLogo_small.png');
        this.setFavicon('images/EngryvLogo_small.png');
    }

    /**
     * Navigate to a view with proper Promise-based locking to prevent race conditions
     * @param {string} viewSlug - Slug of view to load
     */
    loadView = async (viewSlug) => {
        // Debounce rapid navigation attempts - prevent queuing many requests from fast clicks
        // If there's already a pending navigation, cancel it and start fresh with this slug
        if (this.navDebounceTimeout) {
            clearTimeout(this.navDebounceTimeout);
        }

        // Store the slug and set a 50ms debounce window
        // This allows rapid clicks to be consolidated into a single navigation
        this.pendingNavSlug = viewSlug;
        this.navDebounceTimeout = setTimeout(() => {
            this.navDebounceTimeout = null;
            // Proceed with the debounced navigation
            this.#performNavigation(viewSlug);
        }, 50);
    };

    /**
     * Perform the actual navigation after debounce period
     * @private
     * @param {string} viewSlug - Slug of view to load
     */
    #performNavigation = async (viewSlug) => {
        // Check for product/guidebook routes early
        const isProductRoute = viewSlug.startsWith('product/');
        const isGuideBookRoute = viewSlug.includes('/guidebook');
        const isSpecialRoute = ['next-idea-blog', 'cookie-settings'].includes(viewSlug);

        // Prevent duplicate navigation BEFORE queueing (early exit)
        if (this.currentViewSlug() === viewSlug && !isProductRoute) {
            return;
        }

        // Mark as "in progress" BEFORE queuing to prevent race conditions
        this.currentViewSlug(viewSlug);

        // Queue navigation using navigationLock to prevent overlapping executions
        const navigationPromise = this.navigationLock.then(async () => {
            try {
                this.isTransitioning(true);
                const mainContent = document.getElementById('main-content');
                
                // Set view type early for guidebook so CSS classes apply BEFORE transition
                if (isGuideBookRoute) {
                    this.currentViewType(VIEW_TYPES.PRODUCT_GUIDE_BOOK);
                    if (mainContent) {
                        mainContent.removeAttribute('style');
                    }
                }
                
                // Preload product cards only for the Products page (before fade starts)
                // This ensures availableProductTypes is populated and navbar buttons are ready
                // before the fade-in animation begins, preventing resize glitches
                if (viewSlug === 'productsInfo') {
                    // Clear product detail state when returning to products list
                    this.productViewModel.currentProduct(null);
                    this.currentViewType(VIEW_TYPES.DEFAULT);
                    await this.productViewModel.loadDisplayCards();
                    this.displayCards(this.productViewModel.displayCards());
                }
                
                this.transitionManager.startTransition(mainContent, this.currentViewType());
                
                // Update browser URL for this view (needed for refresh/bookmarking to work)
                // Only add a history entry if the URL doesn't already show this view:
                // back/forward, hash links and cinematicProductNav have already set it,
                // and pushing again leaves a duplicate entry (back needs two clicks)
                const currentPathSlug = window.location.pathname.replace(/^\//, '') || 'home';
                if (currentPathSlug !== viewSlug) {
                    const newUrl = window.location.origin + '/' + viewSlug;
                    window.history.pushState({ slug: viewSlug }, '', newUrl);
                }
                
                // Wait for fade-out transition to complete (CSS transition is 500ms, added 50ms buffer)
                await this.transitionManager.delay(550);
                
                // currentViewSlug already set above to prevent race conditions
                this.viewNotFound(false);
                this.#updateLogo(viewSlug);
                
                // Handle guidebook routes
                if (isGuideBookRoute) {
                    const result = await this.navigationService.handleProductGuideBookView(viewSlug);
                    if (result.success) {
                        this.currentViewData({ title: result.title, htmlContent: result.htmlContent });
                        document.title = result.docTitle;
                        this.logoImgSrc(result.logoSrc);
                        this.setFavicon(result.logoFavicon);
                        this.currentViewType(result.viewType);
                        
                        // Wait for guidebook preloading
                        await this.navigationService.waitForGuideBookPreloading();
                        this.#openGuideBookCategoryFromUrl();
                    } else {
                        this.currentViewData({ title: result.title, htmlContent: result.htmlContent });
                        this.logoImgSrc(result.logoSrc);
                        this.setFavicon(result.logoFavicon);
                        this.viewNotFound(true);
                        this.navigationService.hideProductUI();
                    }
                    this.transitionManager.endTransition(mainContent, this.currentViewType());
                    return;
                }
                
                // Handle product detail routes
                if (isProductRoute) {
                    const result = await this.navigationService.handleProductView(viewSlug);
                    if (result.success) {
                        this.currentViewData({ title: result.title, htmlContent: result.htmlContent });
                        document.title = result.docTitle;
                        this.logoImgSrc(result.logoSrc);
                        this.setFavicon(result.logoFavicon);
                        this.currentViewType(result.viewType);
                    } else {
                        this.currentViewData({ title: result.title, htmlContent: result.htmlContent });
                        this.logoImgSrc(result.logoSrc);
                        this.setFavicon(result.logoFavicon);
                        this.viewNotFound(true);
                        this.navigationService.hideProductUI();
                    }
                    this.transitionManager.endTransition(mainContent, this.currentViewType());
                    return;
                }
                
                // Handle cookie settings
                if (viewSlug === 'cookie-settings') {
                    const cookieTitle = (typeof window.Engryv.i18n.t === 'function' && window.Engryv.i18n.t('cookie.settingsTitle')) || 'Cookie Settings';
                    this.currentViewData({ title: cookieTitle, htmlContent: '' });
                    document.title = cookieTitle;
                    this.currentViewType(VIEW_TYPES.DEFAULT);
                    this.navigationService.hideProductUI();
                    this.transitionManager.endTransition(mainContent, this.currentViewType());
                    return;
                }

                // Handle idea form - load through site content first
                if (viewSlug === 'next-idea-blog') {
                    this.currentViewType(VIEW_TYPES.DEFAULT);
                    this.navigationService.hideProductUI();
                    
                    try {
                        await this.loadSiteContentView(viewSlug);
                        // Initialize form after content is loaded and bound
                        if (window.appViewModel && window.appViewModel.ideaFormViewModel && 
                            typeof window.appViewModel.ideaFormViewModel.initializeIdeaForm === 'function') {
                            setTimeout(() => {
                                try {
                                    window.appViewModel.ideaFormViewModel.initializeIdeaForm();
                                } catch (formErr) {
                                    errorHandler.handle(formErr, { viewSlug, stage: 'initializeIdeaForm' }, 'error');
                                }
                            }, 100);
                        }
                    } catch (err) {
                        errorHandler.handle(err, { viewSlug, stage: 'loadSiteContentView' }, 'error');
                        this.#displayErrorView('form');
                    }
                    this.transitionManager.endTransition(mainContent, this.currentViewType());
                    return;
                }

                // Handle regular site content views
                try {
                    this.navigationService.hideProductUI();
                    
                    const legalPages = ['privacy-policy', 'terms-of-service'];
                    if (legalPages.includes(viewSlug)) {
                        this.currentViewType(VIEW_TYPES.LEGAL);
                    } else {
                        this.currentViewType(VIEW_TYPES.DEFAULT);
                        this.currentViewData({ title: '', htmlContent: '' });
                    }
                    
                    await this.loadSiteContentView(viewSlug);
                } catch (err) {
                    errorHandler.handle(err, { viewSlug, stage: 'loadSiteContentView' }, 'error');
                    this.#displayErrorView('content');
                    this.currentViewType(VIEW_TYPES.DEFAULT);
                }
                
                // End transition and trigger fade-in
                // Products data already preloaded before fade started
                this.transitionManager.endTransition(mainContent, this.currentViewType());
            } catch (error) {
                errorHandler.handle(error, { viewSlug, stage: 'loadView' }, 'error');
                // Reset currentViewSlug to allow re-navigation attempt on error
                this.currentViewSlug('');
                this.#displayErrorView('generic');
                this.transitionManager.endTransition(document.getElementById('main-content'), this.currentViewType());
            } finally {
                this.isTransitioning(false);
                this.isLoadingView(false);
            }
        });

        // Update the lock for the next navigation, catch any errors to keep chain alive
        this.navigationLock = navigationPromise.catch(() => {
            // Ignore errors in lock chain - error is handled above
        });
        
        return navigationPromise;
    };
}

export default AppViewModel;
